'use client'
import { useState, type ComponentPropsWithoutRef } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism'

interface ICollapsibleTextProps {
  text: string
  className?: string
  expandButtonClassName?: string
  defaultExpanded?: boolean
  textLimit?: number
  expandText?: string
  collapseText?: string
  lineClamp?: number
  renderMarkdown?: boolean
  codeTheme?: any // Theme for code syntax highlighting
}

// Type for the code component in ReactMarkdown
interface CodeProps extends ComponentPropsWithoutRef<'code'> {
  inline?: boolean;
  className?: string;
}

export const CollapsibleText = ({
  text,
  className,
  expandButtonClassName,
  defaultExpanded = false,
  textLimit = 300,
  lineClamp = 3,
  expandText = 'Xem thêm',
  collapseText = 'Thu gọn',
  renderMarkdown = true,
  codeTheme = vscDarkPlus,
}: ICollapsibleTextProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const shouldShowCollapse = text.length > textLimit

  // Error boundary wrapper for ReactMarkdown
  const renderContent = () => {
    try {
      if (renderMarkdown) {
        return (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              p: ({ node, ...props }) => <p className="my-2" {...props} />,
              h1: ({ node, ...props }) => <h1 className="mt-4 mb-2 font-bold text-2xl" {...props} />,
              h2: ({ node, ...props }) => <h2 className="mt-4 mb-2 font-bold text-xl" {...props} />,
              h3: ({ node, ...props }) => <h3 className="mt-4 mb-2 font-bold text-lg" {...props} />,
              ul: ({ node, ...props }) => <ul className="my-2 list-disc pl-6" {...props} />,
              ol: ({ node, ...props }) => <ol className="my-2 list-decimal pl-6" {...props} />,
              a: ({ node, ...props }) => (
                <a
                  className="text-teal-600 underline hover:text-teal-700"
                  target="_blank"
                  rel="noopener noreferrer"
                  {...props}
                />
              ),
              code: ({ inline, className, children, ...props }: CodeProps) => {
                const match = /language-(\w+)/.exec(className || '')
                return !inline && match ? (
                  <div className="my-4 overflow-hidden rounded-md">
                    <SyntaxHighlighter
                      style={codeTheme}
                      language={match[1]}
                      PreTag="div"
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  </div>
                ) : (
                  <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-sm dark:bg-gray-800" {...props}>
                    {children}
                  </code>
                )
              },
              table: ({ node, ...props }) => (
                <div className="my-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => <thead className="bg-gray-50 dark:bg-gray-800" {...props} />,
              th: ({ node, ...props }) => <th className="px-4 py-2 text-left font-medium text-sm" {...props} />,
              td: ({ node, ...props }) => <td className="px-4 py-2 text-sm" {...props} />,
            }}
          >
            {text}
          </ReactMarkdown>
        )
      }
      return text
    } catch (error) {
      // Log error but don't crash the page
      console.error('Error rendering CollapsibleText:', {
        error,
        renderMarkdown,
        textLength: text?.length,
        isExpanded,
      })
      // Fallback to plain text
      return <div className="text-gray-700">{text}</div>
    }
  }

  return (
    <div className="relative">
      <motion.div
        key={isExpanded ? 'expanded' : 'collapsed'}
        initial={false}
        animate={{
          height: isExpanded ? 'auto' : undefined,
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
        }}
        className={cn(
          'prose prose-lg max-w-none whitespace-pre-line overflow-hidden',
          !isExpanded && shouldShowCollapse ? `line-clamp-${lineClamp}` : '',
          className
        )}
      >
        {renderContent()}
      </motion.div>

      {shouldShowCollapse && (
        <div className="mt-4">
          <Button
            variant="ghost"
            className={cn(
              "flex items-center gap-2 text-teal-600 hover:text-teal-700",
              expandButtonClassName
            )}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                {collapseText} <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                {expandText} <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
} 